/**
 * One device's settings.
 *
 * Every section is either following the family defaults or customised here,
 * rendered through InheritSection. What this page writes is the device's
 * override; the resolved document that actually reaches the endpoint is
 * computed on save (see `data/deviceConfig.ts`).
 *
 * The collection toggle saves immediately — "pause Sam's laptop, it's
 * dinner" is not a two-step flow. Everything else edits a local draft and
 * saves together.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { InheritSection } from '../components/InheritSection';
import { ScheduleEditor } from '../components/ScheduleEditor';
import { TaxonomyEditor } from '../components/TaxonomyEditor';
import {
  currentConfig,
  fetchLatest,
  overrideCount,
  resolve,
  save as saveFamilyConfig,
  type Blocklist,
  type DeviceOverride,
} from '../data/config';
import {
  endpoints,
  refresh,
  removeEndpoint,
  supportsDeviceConfig,
  DEVICE_CONFIG_MIN_VERSION,
} from '../data/endpoints';
import { setDeviceOverride } from '../data/deviceConfig';
import {
  summarizeBlocklists,
  summarizeCadence,
  summarizeRetention,
  summarizeSchedule,
  summarizeTaxonomy,
  taxonomyLabels,
} from '../lib/configSummary';

/** Blocklist domains as newline-separated text for the textarea. */
function blocklistToText(blocklists: Blocklist[]): string {
  const first = blocklists[0];
  if (first === undefined) return '';
  return first.entries
    .map((e) => ('domain' in e ? e.domain : ''))
    .filter((s) => s.length > 0)
    .join('\n');
}

/** Textarea text back into a single named blocklist, de-duped. */
function textToBlocklists(text: string): Blocklist[] {
  const seen = new Set<string>();
  const entries = [];
  for (const line of text.split('\n')) {
    const host = line.trim();
    if (host.length === 0 || seen.has(host)) continue;
    seen.add(host);
    entries.push({ domain: host });
  }
  return entries.length > 0 ? [{ id: 'main', label: 'Main', entries }] : [];
}

export function DeviceRoute() {
  const { params } = useRoute();
  const location = useLocation();
  const endpointId = params.id ?? '';

  const [draft, setDraft] = useState<DeviceOverride | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    void refresh();
    void fetchLatest();
  }, []);

  const cfg = currentConfig.value;
  const device = endpoints.value.find((e) => e.id === endpointId);

  // The saved override is the source of truth; `draft` is the unsaved edit
  // on top of it. Seeded lazily so a mid-edit background refresh doesn't
  // stomp what the parent is typing.
  const savedOverride = cfg?.device_overrides[endpointId];
  const working: DeviceOverride = draft ?? savedOverride ?? {};
  const dirty =
    draft !== null &&
    JSON.stringify(draft) !== JSON.stringify(savedOverride ?? {});

  const resolved = useMemo(
    () => (cfg === null ? null : resolve(cfg, working)),
    [cfg, working],
  );

  function patch(next: DeviceOverride): void {
    setDraft(next);
    setSaved(false);
  }

  /** Turn a section on for this device, seeded from the family value. */
  function customise<K extends keyof DeviceOverride>(
    key: K,
    seed: DeviceOverride[K],
  ): void {
    patch({ ...working, [key]: seed });
  }

  /** Drop a section's override so it follows the family default again. */
  function without(key: keyof DeviceOverride): DeviceOverride {
    const { [key]: _cleared, ...rest } = working;
    return rest;
  }

  function reset(key: keyof DeviceOverride): void {
    patch(without(key));
  }

  async function commit(override: DeviceOverride): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await setDeviceOverride(endpointId, override, saveFamilyConfig);
      setDraft(null);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /** The kill switch saves on click rather than waiting for Save. */
  async function toggleCollection(): Promise<void> {
    if (resolved === null) return;
    await commit({
      ...working,
      collection_enabled: !resolved.collection_enabled,
    });
  }

  async function onRemove(): Promise<void> {
    await removeEndpoint(endpointId);
    location.route('/dashboard');
  }

  if (cfg === null || resolved === null) {
    return (
      <main class="mx-auto max-w-3xl p-4">
        <p class="text-sm text-surface-muted">Loading…</p>
      </main>
    );
  }

  if (device === undefined) {
    return (
      <main class="mx-auto max-w-3xl space-y-4 p-4">
        <a href="/dashboard" class="text-sm text-surface-muted">
          ← Dashboard
        </a>
        <Card>This device is no longer enrolled.</Card>
      </main>
    );
  }

  const family = cfg;
  const stale = !supportsDeviceConfig(device.agent_version);
  const customCount = overrideCount(savedOverride);

  return (
    <main class="mx-auto max-w-3xl space-y-4 p-4">
      <a
        href="/dashboard"
        class="inline-block text-sm text-surface-muted hover:text-surface-fg"
      >
        ← Dashboard
      </a>

      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 class="text-3xl font-semibold">
            {device.label ?? 'Unnamed device'}
          </h1>
          <p class="text-sm text-surface-muted">
            {device.platform} · v{device.agent_version}
          </p>
        </div>
        <div class="flex items-center gap-3">
          {customCount > 0 ? (
            <Badge tone="danger">{customCount} custom</Badge>
          ) : (
            <Badge>Family defaults</Badge>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setConfirmRemove(true);
            }}
          >
            Remove device
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title="Remove device"
        body="This device will stop reporting immediately. It would need to be re-installed to reconnect. Your saved alerts are kept."
        confirmLabel="Remove"
        destructive
        onConfirm={onRemove}
      />

      {/* The honesty requirement: an override on an old binary is stored
          but not applied, and saying nothing would read as "the product is
          broken" the first time a parent checked. */}
      {stale && customCount > 0 ? (
        <div class="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800">
          <span class="font-semibold">
            Custom settings saved, but not applied.
          </span>{' '}
          This device needs Backstop {DEVICE_CONFIG_MIN_VERSION} or later — it's
          on {device.agent_version}. It's still running the family defaults
          until it updates.
        </div>
      ) : null}

      {error !== null ? (
        <div class="rounded border border-signal/40 bg-signal-bg p-3 text-sm text-signal">
          Save failed: {error}
        </div>
      ) : null}

      <InheritSection
        label="Collection"
        overridden={working.collection_enabled !== undefined}
        busy={busy}
        summary={
          <div class="flex items-center justify-between gap-4">
            <span>
              {resolved.collection_enabled
                ? 'On for this device'
                : 'Paused on this device'}
            </span>
            <Button
              size="sm"
              variant={resolved.collection_enabled ? 'danger' : 'primary'}
              disabled={busy}
              onClick={() => void toggleCollection()}
            >
              {resolved.collection_enabled ? 'Pause' : 'Resume'}
            </Button>
          </div>
        }
        familySummary={family.collection_enabled ? 'on' : 'paused'}
        onCustomise={() => {
          customise('collection_enabled', family.collection_enabled);
        }}
        onReset={() => {
          // Commits straight away, matching the toggle above it: this
          // section's changes never wait for the Save button.
          void commit(without('collection_enabled'));
        }}
      />

      <InheritSection
        label="Schedule"
        overridden={working.schedule !== undefined}
        busy={busy}
        summary={summarizeSchedule(resolved.schedule)}
        familySummary={summarizeSchedule(family.schedule)}
        onCustomise={() => {
          customise('schedule', family.schedule);
        }}
        onReset={() => {
          reset('schedule');
        }}
      >
        <ScheduleEditor
          value={resolved.schedule}
          onChange={(schedule) => {
            patch({ ...working, schedule });
          }}
        />
      </InheritSection>

      <InheritSection
        label="Screenshot interval"
        overridden={working.cadence !== undefined}
        busy={busy}
        summary={summarizeCadence(resolved.cadence)}
        familySummary={summarizeCadence(family.cadence)}
        onCustomise={() => {
          customise('cadence', family.cadence);
        }}
        onReset={() => {
          reset('cadence');
        }}
      >
        <label class="flex items-center gap-2 text-sm">
          Seconds between captures
          <input
            type="number"
            min={30}
            max={3600}
            value={resolved.cadence.active_seconds}
            class="h-9 w-24 rounded border border-surface-muted/40 bg-surface px-2 text-sm"
            onInput={(e) => {
              const n = Number((e.target as HTMLInputElement).value);
              if (Number.isFinite(n) && n > 0) {
                patch({
                  ...working,
                  cadence: { ...resolved.cadence, active_seconds: n },
                });
              }
            }}
          />
        </label>
      </InheritSection>

      <InheritSection
        label="Harms taxonomy"
        overridden={working.taxonomy !== undefined}
        busy={busy}
        summary={
          <>
            {summarizeTaxonomy(resolved.taxonomy)}
            <div class="mt-1 text-xs font-normal text-surface-muted">
              {taxonomyLabels(resolved.taxonomy)}
            </div>
          </>
        }
        familySummary={summarizeTaxonomy(family.taxonomy)}
        onCustomise={() => {
          customise('taxonomy', family.taxonomy);
        }}
        onReset={() => {
          reset('taxonomy');
        }}
      >
        <TaxonomyEditor
          value={resolved.taxonomy}
          showPresets={false}
          onChange={(taxonomy) => {
            patch({ ...working, taxonomy });
          }}
        />
      </InheritSection>

      <InheritSection
        label="Blocklist"
        overridden={working.blocklists !== undefined}
        busy={busy}
        summary={summarizeBlocklists(resolved.blocklists)}
        familySummary={summarizeBlocklists(family.blocklists)}
        onCustomise={() => {
          customise('blocklists', family.blocklists);
        }}
        onReset={() => {
          reset('blocklists');
        }}
      >
        <label class="block text-sm">
          <span class="sr-only">Blocklist domains</span>
          <textarea
            aria-label="Blocklist domains"
            class="h-32 w-full rounded border border-surface-muted/40 bg-surface p-2 font-mono text-sm"
            value={blocklistToText(resolved.blocklists)}
            onInput={(e) => {
              patch({
                ...working,
                blocklists: textToBlocklists(
                  (e.target as HTMLTextAreaElement).value,
                ),
              });
            }}
          />
        </label>
      </InheritSection>

      <InheritSection
        label="Local retention"
        overridden={working.retention_days !== undefined}
        busy={busy}
        summary={summarizeRetention(resolved.retention_days)}
        familySummary={summarizeRetention(family.retention_days)}
        onCustomise={() => {
          customise('retention_days', family.retention_days);
        }}
        onReset={() => {
          reset('retention_days');
        }}
      >
        <label class="flex items-center gap-2 text-sm">
          Days of screenshots kept on the device
          <input
            type="number"
            min={1}
            max={90}
            value={resolved.retention_days}
            class="h-9 w-24 rounded border border-surface-muted/40 bg-surface px-2 text-sm"
            onInput={(e) => {
              const n = Number((e.target as HTMLInputElement).value);
              if (Number.isFinite(n) && n > 0) {
                patch({ ...working, retention_days: n });
              }
            }}
          />
        </label>
      </InheritSection>

      <Card class="bg-surface-2 text-sm text-surface-muted">
        The LLM provider, model, and API key are set once for the whole family
        under{' '}
        <a href="/settings" class="underline">
          Settings
        </a>{' '}
        — they aren't per device.
      </Card>

      <div class="flex items-center gap-3">
        <Button disabled={!dirty || busy} onClick={() => void commit(working)}>
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
        {dirty ? (
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setDraft(null);
            }}
          >
            Discard
          </Button>
        ) : null}
        {saved && !dirty ? (
          <span class="text-sm text-emerald-700">Saved.</span>
        ) : null}
      </div>
    </main>
  );
}
